import React, { useRef } from 'react';
import { Grid, IconButton, makeStyles, colors } from '@material-ui/core';
import { CloudUpload } from '@material-ui/icons';
import { useSnackbar } from 'notistack';

import { MAX_FILE_SIZE } from 'domain/recipe/usecases/validator';

import Description from 'components/typography/Description';

import CoverPhotoDeleteButton from './CoverPhotoDeleteButton';

const black87 = 'rgba(0,0,0,0.48)';

const useStyles = makeStyles({
  uploadButton: {
    color: colors.red[400],
    '&:hover': {
      background: colors.red[100],
    },
  },
  emptyContainer: {
    width: 320,
    height: 240,
    borderColor: black87,
    borderWidth: '1.5px',
    borderStyle: 'dotted',
    borderRadius: '8px',
  },
  container: {
    width: 320,
    height: 240,
    borderRadius: '8px',
    boxShadow: '0px 1px 4px 2px rgba(0,0,0,0.24)',
  },
  imageContainer: {
    width: '100%',
    height: '100%',
  },
  image: {
    width: 'inherit',
    height: 'inherit',
    objectFit: 'cover',
    borderRadius: '8px',
  },
});

const CoverPhotoInput = ({
  coverPhoto,
  setCoverPhoto,
  errorMessage,
  readOnly = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const fileInput = useRef(null);
  const classes = useStyles();

  const onChange = async (event) => {
    const newFile = event.target.files[0];

    if (newFile) {
      if (newFile.size > MAX_FILE_SIZE) {
        fileInput.current.value = null;
        enqueueSnackbar('File should not be larger than 500 kB', {
          variant: 'error',
        });
        return;
      }

      const fileReader = new FileReader();

      fileReader.onload = (e) => setCoverPhoto(e.target.result);

      fileReader.readAsDataURL(newFile);
    }
  };

  const onDeleteButtonClick = () => {
    fileInput.current.value = null;
    setCoverPhoto('');
  };

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item container alignItems="center" spacing={2}>
        <Grid item>
          <Description>
            <b>Cover photo</b>
          </Description>
        </Grid>

        {!readOnly && (
          <Grid item>
            <input
              accept="image/png,image/jpg,image/jpeg"
              id="icon-button-file"
              type="file"
              onChange={onChange}
              style={{ display: 'none' }}
              ref={fileInput}
            />
            <label htmlFor="icon-button-file">
              <IconButton className={classes.uploadButton} component="span">
                <CloudUpload></CloudUpload>
              </IconButton>
            </label>
          </Grid>
        )}

        {!readOnly && coverPhoto && (
          <Grid item>
            <CoverPhotoDeleteButton
              onDeleteButtonClick={onDeleteButtonClick}
            ></CoverPhotoDeleteButton>
          </Grid>
        )}
      </Grid>

      <Grid item container justify="center" style={{ position: 'relative' }}>
        {!coverPhoto && <EmptyCoverPhoto></EmptyCoverPhoto>}
        {coverPhoto && <PhotoSelected coverPhoto={coverPhoto}></PhotoSelected>}
      </Grid>
    </Grid>
  );
};

const EmptyCoverPhoto = () => {
  const classes = useStyles();

  return (
    <div className={classes.emptyContainer}>
      <Grid
        container
        justify="center"
        alignItems="center"
        style={{ height: '100%' }}
      >
        <Grid item>
          <Description>
            <b>No</b> cover photo selected
          </Description>
        </Grid>
      </Grid>
    </div>
  );
};

const PhotoSelected = ({ coverPhoto }) => {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      <img src={coverPhoto} alt="Recipe Cover" className={classes.image} />
    </div>
  );
};

export default CoverPhotoInput;
